import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import {
  getAdminBranding, updateAdminBranding,
  getAdminContact,  updateAdminContact,
  getAdminLegal,    updateAdminLegal,
  getAdminFAQs,     createAdminFAQ, updateAdminFAQ, deleteAdminFAQ, reorderAdminFAQs,
  getAppVersion,    updateAppVersion,
} from "../../../api/apiServices";

// ── Helpers ────────────────────────────────────────────────────
const TABS = [
  { id: "branding",    label: "Branding",    icon: "ri-palette-line"        },
  { id: "contact",     label: "Contact",     icon: "ri-contacts-line"       },
  { id: "legal",       label: "Legal",       icon: "ri-file-shield-2-line"  },
  { id: "faq",         label: "FAQ",         icon: "ri-question-answer-line"},
  { id: "app_version", label: "App Version", icon: "ri-smartphone-line"     }, // ADDED
];

const inputCls = "w-full bg-[#111] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/50 transition-colors";
const labelCls = "text-white/40 text-[10px] uppercase tracking-wide block mb-1.5 font-semibold";

function Field({ label, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

function SaveBar({ loading, onSave, dirty }) {
  if (!dirty) return null;
  return (
    <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/5 mt-4">
      <p className="text-white/30 text-xs flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
        Unsaved changes
      </p>
      <button
        onClick={onSave}
        disabled={loading}
        className="px-5 py-2.5 bg-[#00C896] hover:bg-[#00b386] text-black text-sm font-bold rounded-xl disabled:opacity-50 flex items-center gap-2 transition-colors"
      >
        {loading
          ? <><div className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin" />Saving…</>
          : <><i className="ri-save-line" />Save Changes</>}
      </button>
    </div>
  );
}

// ── Confirm Modal ──────────────────────────────────────────────
function ConfirmModal({ title, message, onConfirm, onCancel, loading }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="px-6 pt-6 pb-4 text-center space-y-2">
          <p className="text-white font-bold">{title}</p>
          <p className="text-white/40 text-sm leading-relaxed">{message}</p>
        </div>
        <div className="flex gap-2 px-6 pb-6">
          <button onClick={onCancel} disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10 transition-colors">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" /> : null}
            {loading ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// TAB: BRANDING
// ══════════════════════════════════════════════════════════════
function BrandingTab() {
  const [form, setForm]     = useState({ name: "", logoUrl: "", primaryColor: "#0f4c35", secondaryColor: "#00C896", tagline: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);
  const original              = useRef({});

  useEffect(() => {
    getAdminBranding()
      .then(res => {
        if (res.status) { setForm(res.data); original.current = res.data; }
      })
      .catch(() => toast.error("Failed to load branding"))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setDirty(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAdminBranding(form);
      original.current = form;
      setDirty(false);
      toast.success("Branding saved");
    } catch { toast.error("Failed to save branding"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-12 text-center text-white/20 animate-pulse text-sm">Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="App Name">
          <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Easwallet" className={inputCls} />
        </Field>
        <Field label="Tagline">
          <input value={form.tagline} onChange={e => set("tagline", e.target.value)} placeholder="Buy, Sell & Store Crypto in Nigeria" className={inputCls} />
        </Field>
      </div>

      <Field label="Logo URL">
        <div className="flex gap-3 items-start">
          <input value={form.logoUrl} onChange={e => set("logoUrl", e.target.value)} placeholder="https://cdn.yourdomain.com/logo.png" className={inputCls} />
          {form.logoUrl && (
            <img src={form.logoUrl} alt="logo preview" className="w-12 h-12 rounded-xl object-contain bg-white/5 border border-white/10 flex-shrink-0" />
          )}
        </div>
        <p className="text-white/25 text-[10px] mt-1">Upload your logo to a CDN and paste the URL here</p>
      </Field>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Primary Colour">
          <div className="flex items-center gap-3 bg-[#111] border border-white/10 rounded-xl px-4 py-3">
            <input type="color" value={form.primaryColor} onChange={e => set("primaryColor", e.target.value)}
              className="w-8 h-8 rounded-lg border-0 cursor-pointer bg-transparent" />
            <input value={form.primaryColor} onChange={e => set("primaryColor", e.target.value)}
              placeholder="#0f4c35" className="flex-1 bg-transparent text-sm text-white focus:outline-none font-mono" />
          </div>
        </Field>
        <Field label="Secondary / Accent Colour">
          <div className="flex items-center gap-3 bg-[#111] border border-white/10 rounded-xl px-4 py-3">
            <input type="color" value={form.secondaryColor} onChange={e => set("secondaryColor", e.target.value)}
              className="w-8 h-8 rounded-lg border-0 cursor-pointer bg-transparent" />
            <input value={form.secondaryColor} onChange={e => set("secondaryColor", e.target.value)}
              placeholder="#00C896" className="flex-1 bg-transparent text-sm text-white focus:outline-none font-mono" />
          </div>
        </Field>
      </div>

      <div className="bg-white/4 border border-white/5 rounded-xl p-4">
        <p className={labelCls}>Preview</p>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: form.primaryColor }}>
            {form.logoUrl
              ? <img src={form.logoUrl} className="w-8 h-8 object-contain" alt="" />
              : <i className="ri-wallet-3-line text-white text-xl" />}
          </div>
          <div>
            <p className="text-white font-bold text-sm">{form.name || "Easwallet"}</p>
            <p className="text-xs" style={{ color: form.secondaryColor }}>{form.tagline || "Your tagline here"}</p>
          </div>
          <div className="ml-auto px-4 py-2 rounded-xl text-sm font-bold text-black" style={{ background: form.secondaryColor }}>
            Button
          </div>
        </div>
      </div>

      <SaveBar loading={saving} onSave={handleSave} dirty={dirty} />
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// TAB: CONTACT
// ══════════════════════════════════════════════════════════════
const CONTACT_FIELDS = [
  { key: "phone",    label: "Phone Number",    icon: "ri-phone-line",    placeholder: "+2348012345678"         },
  { key: "whatsapp", label: "WhatsApp Number", icon: "ri-whatsapp-line", placeholder: "+2348012345678"         },
  { key: "email",    label: "Support Email",   icon: "ri-mail-line",     placeholder: "support@easwallet.app"  },
  { key: "address",  label: "Office Address",  icon: "ri-map-pin-line",  placeholder: "123 Victoria Island, Lagos" },
];
const SOCIAL_FIELDS = [
  { key: "x",         label: "Twitter / X", icon: "ri-twitter-x-line",  placeholder: "easwallet (no @)"  },
  { key: "instagram", label: "Instagram",   icon: "ri-instagram-line",   placeholder: "easwallet (no @)"  },
  { key: "facebook",  label: "Facebook",    icon: "ri-facebook-line",    placeholder: "easwallet.app"     },
  { key: "tiktok",    label: "TikTok",      icon: "ri-tiktok-line",      placeholder: "easwallet (no @)"  },
  { key: "youtube",   label: "YouTube",     icon: "ri-youtube-line",     placeholder: "easwallet"         },
];

function ContactTab() {
  const blank = { phone:"", whatsapp:"", email:"", address:"", x:"", instagram:"", facebook:"", tiktok:"", youtube:"" };
  const [form, setForm]     = useState(blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  useEffect(() => {
    getAdminContact()
      .then(res => { if (res.status) setForm(res.data); })
      .catch(() => toast.error("Failed to load contact info"))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setDirty(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAdminContact(form);
      setDirty(false);
      toast.success("Contact info saved");
    } catch { toast.error("Failed to save contact info"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-12 text-center text-white/20 animate-pulse text-sm">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-white text-sm font-semibold mb-3">Contact Details</p>
        <div className="space-y-3">
          {CONTACT_FIELDS.map(f => (
            <Field key={f.key} label={f.label}>
              <div className="relative">
                <i className={`${f.icon} absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 text-sm`} />
                <input value={form[f.key]} onChange={e => set(f.key, e.target.value)}
                  placeholder={f.placeholder} className={inputCls + " pl-9"} />
              </div>
            </Field>
          ))}
        </div>
      </div>

      <div>
        <p className="text-white text-sm font-semibold mb-3">Social Media</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SOCIAL_FIELDS.map(f => (
            <Field key={f.key} label={f.label}>
              <div className="relative">
                <i className={`${f.icon} absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 text-sm`} />
                <input value={form[f.key]} onChange={e => set(f.key, e.target.value)}
                  placeholder={f.placeholder} className={inputCls + " pl-9"} />
              </div>
            </Field>
          ))}
        </div>
      </div>

      {(form.x || form.instagram || form.facebook || form.tiktok || form.youtube) && (
        <div className="bg-white/4 border border-white/5 rounded-xl p-4">
          <p className={labelCls}>Social Links Preview</p>
          <div className="flex gap-3 flex-wrap mt-1">
            {[
              { key: "x",        icon: "ri-twitter-x-line", base: "https://x.com/"           },
              { key: "instagram", icon: "ri-instagram-line", base: "https://instagram.com/"   },
              { key: "facebook",  icon: "ri-facebook-line",  base: "https://facebook.com/"    },
              { key: "tiktok",    icon: "ri-tiktok-line",    base: "https://tiktok.com/@"     },
              { key: "youtube",   icon: "ri-youtube-line",   base: "https://youtube.com/@"    },
            ].filter(s => form[s.key]).map(s => (
              <a key={s.key} href={`${s.base}${form[s.key]}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors text-xs">
                <i className={`${s.icon} text-sm`} />
                {form[s.key]}
              </a>
            ))}
          </div>
        </div>
      )}

      <SaveBar loading={saving} onSave={handleSave} dirty={dirty} />
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// TAB: LEGAL
// ══════════════════════════════════════════════════════════════
function LegalTab() {
  const [activeDoc, setActiveDoc]     = useState("privacy");
  const [privacy,   setPrivacy]       = useState("");
  const [terms,     setTerms]         = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [dirty, setDirty]             = useState(false);

  useEffect(() => {
    getAdminLegal()
      .then(res => {
        if (res.status) {
          setPrivacy(res.data.privacyPolicy || "");
          setTerms(res.data.termsConditions || "");
          setLastUpdated(res.data.lastUpdated);
        }
      })
      .catch(() => toast.error("Failed to load legal content"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = activeDoc === "privacy" ? { privacyPolicy: privacy } : { termsConditions: terms };
      await updateAdminLegal(payload);
      setDirty(false);
      setLastUpdated(new Date().toISOString());
      toast.success(`${activeDoc === "privacy" ? "Privacy Policy" : "Terms & Conditions"} saved`);
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-12 text-center text-white/20 animate-pulse text-sm">Loading…</div>;

  const docTabs          = [{ id: "privacy", label: "Privacy Policy" }, { id: "terms", label: "Terms & Conditions" }];
  const currentValue     = activeDoc === "privacy" ? privacy : terms;
  const setCurrentValue  = activeDoc === "privacy" ? setPrivacy : setTerms;

  return (
    <div className="space-y-4">
      {lastUpdated && (
        <p className="text-white/30 text-xs">
          Last updated: {new Date(lastUpdated).toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" })}
        </p>
      )}
      <div className="flex gap-2 bg-white/4 p-1 rounded-xl w-fit border border-white/5">
        {docTabs.map(t => (
          <button key={t.id} onClick={() => { setActiveDoc(t.id); setDirty(false); }}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeDoc === t.id ? "bg-[#00C896] text-black" : "text-white/40 hover:text-white/70"
            }`}>
            {t.label}
          </button>
        ))}
      </div>
      <p className="text-white/30 text-xs">Supports Markdown. Use **bold**, ## headings, - lists, etc.</p>
      <textarea
        value={currentValue}
        onChange={e => { setCurrentValue(e.target.value); setDirty(true); }}
        rows={24}
        placeholder={`Enter ${activeDoc === "privacy" ? "Privacy Policy" : "Terms & Conditions"} content here…`}
        className="w-full bg-[#111] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/15 focus:outline-none focus:border-[#00C896]/50 transition-colors resize-y font-mono leading-relaxed"
      />
      <SaveBar loading={saving} onSave={handleSave} dirty={dirty} />
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// TAB: FAQ
// ══════════════════════════════════════════════════════════════
const FAQ_CATEGORIES = ["general", "account", "deposit", "withdrawal", "buy/sell", "security", "fees", "other"];

function FAQTab() {
  const [faqs,     setFaqs]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(null);
  const [confirm,  setConfirm]  = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [catFilter, setCatFilter] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    getAdminFAQs()
      .then(res => { if (res.status) setFaqs(res.data); })
      .catch(() => toast.error("Failed to load FAQs"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggleActive = async (faq) => {
    try {
      await updateAdminFAQ(faq._id, { isActive: !faq.isActive });
      setFaqs(prev => prev.map(f => f._id === faq._id ? { ...f, isActive: !faq.isActive } : f));
      toast.success(faq.isActive ? "FAQ hidden" : "FAQ published");
    } catch { toast.error("Failed to update FAQ"); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteAdminFAQ(confirm.id);
      setFaqs(prev => prev.filter(f => f._id !== confirm.id));
      setConfirm(null);
      toast.success("FAQ deleted");
    } catch { toast.error("Failed to delete FAQ"); }
    finally { setDeleting(false); }
  };

  const handleMoveUp = async (faq, idx, sameCat) => {
    if (idx === 0) return;
    const prev = sameCat[idx - 1];
    await reorderAdminFAQs([{ id: faq._id, order: prev.order }, { id: prev._id, order: faq.order }]).catch(() => {});
    load();
  };

  const categories = ["all", ...new Set(faqs.map(f => f.category))];
  const filtered   = catFilter === "all" ? faqs : faqs.filter(f => f.category === catFilter);
  const grouped    = filtered.reduce((acc, f) => { if (!acc[f.category]) acc[f.category] = []; acc[f.category].push(f); return acc; }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 overflow-x-auto">
          {categories.map(c => (
            <button key={c} onClick={() => setCatFilter(c)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap capitalize transition-all ${
                catFilter === c ? "bg-[#00C896] text-black" : "bg-white/5 text-white/40 hover:bg-white/10 hover:text-white/70"
              }`}>
              {c} {c !== "all" && `(${faqs.filter(f => f.category === c).length})`}
            </button>
          ))}
        </div>
        <button onClick={() => setModal({ mode: "add" })}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#00C896] hover:bg-[#00b386] text-black text-xs font-bold rounded-xl transition-colors flex-shrink-0">
          <i className="ri-add-line" />Add FAQ
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_,i) => <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <i className="ri-question-answer-line text-white/10 text-5xl mb-3" />
          <p className="text-white/25 text-sm">No FAQs yet</p>
          <button onClick={() => setModal({ mode: "add" })} className="mt-3 text-[#00C896] text-xs font-semibold">+ Add the first FAQ</button>
        </div>
      ) : (
        Object.entries(grouped).map(([cat, items]) => (
          <div key={cat}>
            <p className="text-white/40 text-[10px] uppercase tracking-wide font-bold mb-2 capitalize">{cat}</p>
            <div className="space-y-2">
              {items.map((faq, idx) => (
                <div key={faq._id} className={`bg-white/4 border rounded-xl p-4 transition-all ${faq.isActive ? "border-white/5" : "border-white/5 opacity-50"}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex flex-col gap-1 flex-shrink-0 mt-0.5">
                      <button onClick={() => handleMoveUp(faq, idx, items)} disabled={idx === 0}
                        className="w-6 h-6 rounded bg-white/5 hover:bg-white/15 flex items-center justify-center text-white/30 disabled:opacity-20 transition-colors">
                        <i className="ri-arrow-up-s-line text-sm" />
                      </button>
                      <button disabled={idx === items.length - 1}
                        className="w-6 h-6 rounded bg-white/5 hover:bg-white/15 flex items-center justify-center text-white/30 disabled:opacity-20 transition-colors">
                        <i className="ri-arrow-down-s-line text-sm" />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-semibold mb-1">{faq.question}</p>
                      <p className="text-white/40 text-xs leading-relaxed line-clamp-2">{faq.answer}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button onClick={() => handleToggleActive(faq)}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${faq.isActive ? "bg-[#00C896]/10 text-[#00C896]" : "bg-white/5 text-white/30"}`}
                        title={faq.isActive ? "Hide" : "Publish"}>
                        <i className={`${faq.isActive ? "ri-eye-line" : "ri-eye-off-line"} text-sm`} />
                      </button>
                      <button onClick={() => setModal({ mode: "edit", faq })}
                        className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/15 flex items-center justify-center text-white/40 hover:text-white transition-colors">
                        <i className="ri-pencil-line text-sm" />
                      </button>
                      <button onClick={() => setConfirm({ id: faq._id, question: faq.question })}
                        className="w-8 h-8 rounded-lg bg-white/5 hover:bg-red-500/20 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors">
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {modal && <FAQModal mode={modal.mode} faq={modal.faq} onClose={() => setModal(null)} onSaved={load} />}
      {confirm && (
        <ConfirmModal title="Delete FAQ" message={`Delete "${confirm.question}"? This cannot be undone.`}
          loading={deleting} onConfirm={handleDelete} onCancel={() => setConfirm(null)} />
      )}
    </div>
  );
}

function FAQModal({ mode, faq, onClose, onSaved }) {
  const [form, setForm] = useState({
    question: faq?.question || "", answer: faq?.answer || "",
    category: faq?.category || "general", isActive: faq?.isActive ?? true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.question.trim() || !form.answer.trim()) { toast.error("Question and answer are required"); return; }
    setSaving(true);
    try {
      if (mode === "add") { await createAdminFAQ(form); toast.success("FAQ created"); }
      else { await updateAdminFAQ(faq._id, form); toast.success("FAQ updated"); }
      onSaved(); onClose();
    } catch { toast.error("Failed to save FAQ"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70">
      <div className="w-full sm:max-w-lg bg-[#161616] border border-white/8 rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <p className="text-white font-bold text-sm">{mode === "add" ? "Add FAQ" : "Edit FAQ"}</p>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10">
            <i className="ri-close-line text-white/60" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Question">
            <input value={form.question} onChange={e => set("question", e.target.value)} placeholder="What is Easwallet?" className={inputCls} />
          </Field>
          <Field label="Answer">
            <textarea value={form.answer} onChange={e => set("answer", e.target.value)} rows={6}
              placeholder="Easwallet is a Nigerian crypto wallet…" className={inputCls + " resize-y leading-relaxed"} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <select value={form.category} onChange={e => set("category", e.target.value)}
                className={inputCls + " appearance-none capitalize"}>
                {FAQ_CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <div className="flex gap-2 h-[46px]">
                {[{ v: true, l: "Published" }, { v: false, l: "Hidden" }].map(o => (
                  <button key={String(o.v)} onClick={() => set("isActive", o.v)}
                    className={`flex-1 rounded-xl text-xs font-bold border transition-all ${
                      form.isActive === o.v
                        ? o.v ? "bg-[#00C896]/15 text-[#00C896] border-[#00C896]/30" : "bg-white/5 text-white/50 border-white/10"
                        : "bg-white/4 text-white/30 border-white/8 hover:bg-white/8"
                    }`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </Field>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-[#00C896] hover:bg-[#00b386] text-black text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <div className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin" /> : null}
              {saving ? "Saving…" : mode === "add" ? "Create FAQ" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// TAB: APP VERSION — ADDED
// ══════════════════════════════════════════════════════════════
// Semver validation: must be x.y.z
function isValidVersion(v) {
  return /^\d+\.\d+\.\d+$/.test((v || "").trim());
}

function PlatformVersionCard({ platform, record, onSaved }) {
  const isAndroid    = platform === "android";
  const platformIcon = isAndroid ? "ri-android-line" : "ri-apple-line";
  const platformLabel = isAndroid ? "Android"        : "iOS";
  const accentColor  = isAndroid ? "#3DDC84"         : "#aaaaaa";

  const [version,      setVersion]      = useState(record?.minimumVersion || "");
  const [forceUpdate,  setForceUpdate]  = useState(record?.forceUpdate    ?? false);
  const [releaseNotes, setReleaseNotes] = useState(record?.releaseNotes   || "");
  const [saving,       setSaving]       = useState(false);
  const [dirty,        setDirty]        = useState(false);

  // Re-sync when parent re-fetches
  useEffect(() => {
    setVersion(record?.minimumVersion || "");
    setForceUpdate(record?.forceUpdate ?? false);
    setReleaseNotes(record?.releaseNotes || "");
    setDirty(false);
  }, [record]);

  const change = (setter) => (val) => { setter(val); setDirty(true); };

  const handleSave = async () => {
    if (!version.trim()) { toast.error("Version is required"); return; }
    if (!isValidVersion(version)) { toast.error("Version must be in format x.y.z e.g. 1.2.0"); return; }
    setSaving(true);
    try {
      await updateAppVersion(platform, { minimumVersion: version.trim(), forceUpdate, releaseNotes });
      toast.success(`${platformLabel} version updated`);
      setDirty(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white/4 border border-white/5 rounded-2xl overflow-hidden">
      {/* Card header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-white/5">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: accentColor + "18" }}>
          <i className={`${platformIcon} text-lg`} style={{ color: accentColor }} />
        </div>
        <div className="flex-1">
          <p className="text-white font-semibold text-sm">{platformLabel}</p>
          <p className="text-white/30 text-[11px]">
            {record?.updatedAt
              ? `Last updated ${new Date(record.updatedAt).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}`
              : "Not yet configured"}
          </p>
        </div>
        {record?.minimumVersion && (
          <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-white/5 text-white/40">
            v{record.minimumVersion}
          </span>
        )}
      </div>

      <div className="p-5 space-y-4">
        {/* Version input */}
        <Field label="Minimum Version">
          <input
            value={version}
            onChange={e => change(setVersion)(e.target.value)}
            placeholder="e.g. 1.2.0"
            className={inputCls}
          />
          <p className="text-white/25 text-[10px] mt-1.5">
            Users below this version will see the update prompt on next launch
          </p>
        </Field>

        {/* Release notes */}
        <Field label={<>Release Notes <span className="text-white/25 normal-case font-normal">(optional)</span></>}>
          <textarea
            value={releaseNotes}
            onChange={e => change(setReleaseNotes)(e.target.value)}
            placeholder={"• Bug fixes and performance improvements\n• New transaction filters"}
            rows={3}
            maxLength={500}
            className={inputCls + " resize-none leading-relaxed"}
          />
          <p className="text-white/25 text-[10px] mt-1">{releaseNotes.length}/500</p>
        </Field>

        {/* Force update toggle */}
        <div className={`flex items-start gap-3 p-4 rounded-xl border transition-all cursor-pointer ${
          forceUpdate ? "bg-red-500/8 border-red-500/20" : "bg-white/3 border-white/5"
        }`} onClick={() => change(setForceUpdate)(!forceUpdate)}>
          {/* Toggle pill */}
          <div className={`w-11 h-6 rounded-full relative flex-shrink-0 mt-0.5 transition-colors ${forceUpdate ? "bg-red-500" : "bg-white/10"}`}>
            <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${forceUpdate ? "left-6" : "left-1"}`} />
          </div>
          <div>
            <p className={`text-sm font-semibold leading-none mb-1 ${forceUpdate ? "text-red-400" : "text-white/60"}`}>
              Force Update
            </p>
            <p className="text-white/30 text-[11px] leading-relaxed">
              {forceUpdate
                ? "⚠️ App is blocked until users update. Use only for breaking changes."
                : "Users can dismiss the prompt and keep using the current version."}
            </p>
          </div>
        </div>

        <SaveBar loading={saving} onSave={handleSave} dirty={dirty} />
      </div>
    </div>
  );
}

function AppVersionTab() {
  const [records, setRecords] = useState({ android: null, ios: null });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAppVersion();
      const map = { android: null, ios: null };
      (data || []).forEach(r => { map[r.platform] = r; });
      setRecords(map);
    } catch {
      toast.error("Failed to load version config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="flex items-start gap-3 bg-[#00C896]/8 border border-[#00C896]/15 rounded-xl px-4 py-3">
        <i className="ri-information-line text-[#00C896] text-base mt-0.5 flex-shrink-0" />
        <p className="text-[#00C896]/80 text-xs leading-relaxed">
          After publishing a new build to the Play Store or App Store, bump the minimum
          version here to match. The update prompt appears automatically on the user's
          next cold launch — no app restart needed.
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(2)].map((_, i) => <div key={i} className="h-56 bg-white/5 rounded-2xl animate-pulse" />)}
        </div>
      ) : (
        <div className="space-y-4">
          <PlatformVersionCard platform="android" record={records.android} onSaved={load} />
          <PlatformVersionCard platform="ios"     record={records.ios}     onSaved={load} />
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// MAIN VIEW
// ══════════════════════════════════════════════════════════════
export default function AppSettingsView() {
  const [activeTab, setActiveTab] = useState("branding");

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-white">App Settings</h2>
        <p className="text-white/40 text-xs mt-0.5">Manage branding, contact info, legal content, FAQs and app versioning</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-white/4 p-1 rounded-2xl border border-white/5 overflow-x-auto">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all flex-1 justify-center ${
              activeTab === tab.id
                ? "bg-[#00C896] text-black shadow-lg shadow-[#00C896]/20"
                : "text-white/40 hover:text-white/70 hover:bg-white/5"
            }`}>
            <i className={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-[#161616] border border-white/5 rounded-2xl p-5 sm:p-6">
        {activeTab === "branding"    && <BrandingTab />}
        {activeTab === "contact"     && <ContactTab />}
        {activeTab === "legal"       && <LegalTab />}
        {activeTab === "faq"         && <FAQTab />}
        {activeTab === "app_version" && <AppVersionTab />}
      </div>
    </div>
  );
}
